import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  getSettingsApi,
  updateGradeStatusApi,
  updateLeadFeaturesApi,
  updatePipelineStatusApi,
  updateCallPolicyApi,
  updateServicesApi,
  updatePipelineFeaturesApi,
  updateAiConfigApi,
} from "../api/settings.api";
import { getStatusUI } from "../config/leadStatus";
import useAuth from "../hooks/useAuth";

const SettingsContext = createContext(null);

const DEFAULT_SETTINGS = {
  grade_status: [],
  pipeline_status: [],
  lead_features: { sources: [], features: [] },
  pipeline_features: [],
  services: [],
  visibility: {},
  maxSla: null,
  ai_config: {
    auto_segregation_enabled: false,
    segregation_prompt: "",
    qualification_criteria: "Identify messages where the user shows interest in digital marketing services like SEO, Ads, or Websites. Hot: Pricing/Quote inquiries. Warm: General service info. Cold: Greetings only. Dropped: Spam/Wrong number.",
    pipeline_conversion_criteria: "Convert to 'Qualified Leads' (pipeline: qualified) if:\n- User asks for specific service prices (SEO, Ads, Web)\n- User provides their email or city for a quote\n- User requests a callback or a formal meeting\n- User says 'I want to start' or 'How do I sign up?'",
    lead_creation_threshold: 0.7,
  },
  dynamicStatus: [],
};

const buildDynamicStatus = (grade, pipeline) => [
  ...grade.map((key) => ({ key, type: "grade", ...getStatusUI(key) })),
  ...pipeline.map((key) => ({
    key,
    type: "pipeline",
    ...getStatusUI(key),
  })),
];

export const SettingsProvider = ({ children }) => {
  const { user, loading } = useAuth();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user?._id) return;

    setReady(false);

    let alive = true;

    (async () => {
      try {
        const res = await getSettingsApi();
        if (!alive) return;
        hydrate(res?.data?.data || {});
      } finally {
        if (alive) setReady(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user?._id, loading]);

  const hydrate = (data) => {
    const sources = data?.lead_features?.sources || [];
    const features = data?.lead_features?.features || [];
    const grade = data?.grade_status || [];
    const pipeline = data?.pipeline_status || [];
    const services = data?.services || [];
    const pipelineFeatures = data?.pipeline_features || [];
    const ai_config = data?.ai_config || DEFAULT_SETTINGS.ai_config;

    const dynamicStatus = [
      ...grade.map((key) => ({ key, type: "grade", ...getStatusUI(key) })),
      ...pipeline.map((key) => ({
        key,
        type: "pipeline",
        ...getStatusUI(key),
      })),
    ];

    setSettings({
      grade_status: grade,
      pipeline_status: pipeline,
      lead_features: { sources, features },
      pipeline_features: pipelineFeatures,
      services,
      visibility: Object.fromEntries(features.map((f) => [f.key, f.enabled])),
      maxSla: data?.call_policy?.sla_first_response_minutes ?? null,
      ai_config,
      dynamicStatus,
    });
  };

  const saveGrade = async (grade_status) => {
    setSettings((p) => ({
      ...p,
      grade_status,
      dynamicStatus: buildDynamicStatus(grade_status, p.pipeline_status),
    }));

    await updateGradeStatusApi({ grade_status });
  };

  const savePipeline = async (pipeline_status) => {
    setSettings((p) => ({
      ...p,
      pipeline_status,
      dynamicStatus: buildDynamicStatus(p.grade_status, pipeline_status),
    }));

    await updatePipelineStatusApi({ pipeline_status });
  };

  const updateLeadFeatures = async ({ features, sources }) => {
    setSettings((p) => ({
      ...p,
      lead_features: { features, sources },
      visibility: Object.fromEntries(features.map((f) => [f.key, f.enabled])),
    }));
    await updateLeadFeaturesApi({ features, sources });
  };

  const updatePipelineFeatures = async (features) => {
    setSettings((p) => ({
      ...p,
      pipeline_features: features,
    }));

    await updatePipelineFeaturesApi({ features });
  };

  const updateSla = async (minutes) => {
    setSettings((p) => ({ ...p, maxSla: minutes }));
    await updateCallPolicyApi({
      sla_first_response_minutes: minutes,
    });
  };

  const updateServices = async (services) => {
    setSettings((p) => ({ ...p, services }));
    await updateServicesApi(services);
  };

  const updateAiConfig = async (ai_config) => {
    setSettings((p) => ({ ...p, ai_config }));
    await updateAiConfigApi({ ai_config });
  };

  const value = useMemo(
    () => ({
      ...settings,
      ready,
      saveGrade,
      savePipeline,
      updateLeadFeatures,
      updateSla,
      updateServices,
      updatePipelineFeatures,
      updateAiConfig,
    }),
    [settings, ready]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
